import { useCallback, useEffect, useMemo, useState } from 'react';

import './kingdom.css';

import {
  KingdomGameState,
  KingdomGuessResponse,
  KingdomJoinResponse,
  api
} from '../../../api';

interface StoredSession {
  code: string;
  playerId: string;
  playerToken: string;
}

const STORAGE_KEY = 'kingdom-game-session';
let speakWordsInvocationId = 0;

function loadStoredSession(): StoredSession | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as StoredSession;
    if (typeof parsed.code === 'string' && typeof parsed.playerToken === 'string') {
      return parsed;
    }
  } catch (err) {
    console.warn('Failed to load stored Kingdom session', err);
  }

  return null;
}

function persistSession(session: StoredSession | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!session) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function formatStatusLabel(state: KingdomGameState | null): string {
  if (!state) {
    return 'Waiting to join a game…';
  }

  switch (state.status) {
    case 'lobby':
      return 'Lobby — gathering players';
    case 'started':
      return 'Started — time to guess the words!';
    case 'completed':
      return 'Completed — all words have been guessed';
    default:
      return 'Unknown status';
  }
}

interface SpeakWordsOptions {
  repeatCount: number;
  rate: number;
  pauseMs: number;
}

function formatOrdinal(value: number): string {
  const remainder = value % 100;
  if (remainder >= 11 && remainder <= 13) {
    return `${value}th`;
  }

  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

function speakWords(
  words: string[],
  onUnavailable: (message: string) => void,
  options: SpeakWordsOptions
): boolean {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    onUnavailable('Speech synthesis is not available in this browser.');
    return false;
  }

  const sequence = words.slice();

  if (sequence.length === 0) {
    onUnavailable('There are no words to read yet.');
    return false;
  }

  const { repeatCount, rate, pauseMs } = options;

  if (!Number.isFinite(repeatCount) || repeatCount < 1) {
    onUnavailable('Set the repeat count to at least 1 before reading the list.');
    return false;
  }

  const normalizedRepeatCount = Math.max(1, Math.floor(repeatCount));
  const normalizedRate = Math.min(Math.max(rate, 0.5), 2);
  const normalizedPause = Math.max(0, Math.min(Math.floor(pauseMs), 10000));

  const invocationId = ++speakWordsInvocationId;
  const isStale = () => invocationId !== speakWordsInvocationId;

  window.speechSynthesis.cancel();

  const synth = window.speechSynthesis;
  let cancelled = false;

  const scheduleNext = (callback: () => void) => {
    if (normalizedPause > 0) {
      window.setTimeout(() => {
        if (!cancelled && !isStale()) {
          callback();
        }
      }, normalizedPause);
    } else {
      callback();
    }
  };

  const speakUtterance = (
    text: string,
    onEnd: (() => void) | null,
    errorMessage: string
  ) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = normalizedRate;
    utterance.pitch = 1;
    utterance.onerror = () => {
      if (!cancelled && !isStale()) {
        cancelled = true;
        onUnavailable(errorMessage);
      }
    };
    if (onEnd) {
      utterance.onend = () => {
        if (!cancelled && !isStale()) {
          onEnd();
        }
      };
    }
    synth.speak(utterance);
  };

  const handleRoundComplete = (roundIndex: number) => {
    if (cancelled || isStale()) {
      return;
    }

    const nextRoundIndex = roundIndex + 1;
    const isFinalRound = nextRoundIndex >= normalizedRepeatCount;

    const messageParts = ['Finished reading the list.'];
    if (isFinalRound) {
      messageParts.push('All done.');
      speakUtterance(
        messageParts.join(' '),
        () => {
          if (!isStale() && !cancelled) {
            onUnavailable('');
          }
        },
        'Failed to read the words aloud.'
      );
      return;
    }

    const upcomingOrdinal = formatOrdinal(nextRoundIndex + 1);
    if (nextRoundIndex + 1 === normalizedRepeatCount) {
      messageParts.push(`Next will be the ${upcomingOrdinal} and final time.`);
    } else {
      messageParts.push(`Now starting the ${upcomingOrdinal} reading.`);
    }

    speakUtterance(
      messageParts.join(' '),
      () => {
        if (!cancelled && !isStale()) {
          scheduleNext(() => speakRound(nextRoundIndex));
        }
      },
      'Failed to read the words aloud.'
    );
  };

  const speakRound = (roundIndex: number) => {
    if (cancelled || isStale()) {
      return;
    }

    let wordIndex = 0;

    const speakNextWord = () => {
      if (cancelled || isStale()) {
        return;
      }

      const utterance = new SpeechSynthesisUtterance(sequence[wordIndex]);
      utterance.rate = normalizedRate;
      utterance.pitch = 1;
      utterance.onerror = () => {
        if (!cancelled && !isStale()) {
          cancelled = true;
          onUnavailable('Failed to read the words aloud.');
        }
      };
      utterance.onend = () => {
        if (cancelled || isStale()) {
          return;
        }

        wordIndex += 1;
        if (wordIndex >= sequence.length) {
          handleRoundComplete(roundIndex);
          return;
        }

        scheduleNext(speakNextWord);
      };

      synth.speak(utterance);
    };

    scheduleNext(speakNextWord);
  };

  const introText = `Starting the word list. There are ${sequence.length} ${
    sequence.length === 1 ? 'word' : 'words'
  }. I will read the list ${
    normalizedRepeatCount === 1 ? 'one time' : `${normalizedRepeatCount} times`
  }.`;

  speakUtterance(
    introText,
    () => {
      if (cancelled || isStale()) {
        return;
      }

      scheduleNext(() => speakRound(0));
    },
    'Failed to start reading the words aloud.'
  );

  return true;
}

export function KingdomGame(): JSX.Element {
  const [session, setSession] = useState<StoredSession | null>(() => loadStoredSession());
  const [pendingCode, setPendingCode] = useState<string>(() => loadStoredSession()?.code ?? '');
  const [nameInput, setNameInput] = useState('');
  const [wordInput, setWordInput] = useState('');
  const [gameState, setGameState] = useState<KingdomGameState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [speechMessage, setSpeechMessage] = useState<string>('');
  const [guessWordId, setGuessWordId] = useState('');
  const [guessPlayerId, setGuessPlayerId] = useState('');
  const [guessFeedback, setGuessFeedback] = useState<string | null>(null);
  const [lastReadSignature, setLastReadSignature] = useState('');
  const [repeatCount, setRepeatCount] = useState(3);
  const [speechRate, setSpeechRate] = useState(1);
  const [pauseDurationMs, setPauseDurationMs] = useState(500);
  const [wordsHidden, setWordsHidden] = useState(false);

  const speakSettings = useMemo(
    () => ({ repeatCount, rate: speechRate, pauseMs: pauseDurationMs }),
    [pauseDurationMs, repeatCount, speechRate]
  );

  useEffect(() => {
    persistSession(session);
  }, [session]);

  const refreshState = useCallback(async (activeSession: StoredSession) => {
    try {
      const state = await api.fetchKingdomState(activeSession.code, activeSession.playerToken);
      setGameState(state);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load game state.';
      setError(message);
    }
  }, []);

  useEffect(() => {
    if (!session) {
      setGameState(null);
      return;
    }

    let cancelled = false;
    let timer: number | undefined;

    const load = async () => {
      try {
        const state = await api.fetchKingdomState(session.code, session.playerToken);
        if (!cancelled) {
          setGameState(state);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load game state.';
          setError(message);
        }
      }
    };

    void load();
    timer = window.setInterval(() => {
      void load();
    }, 4000);

    return () => {
      cancelled = true;
      if (timer) {
        window.clearInterval(timer);
      }
    };
  }, [session]);

  useEffect(() => {
    if (!gameState || gameState.status === 'lobby') {
      return;
    }

    const signature = `${gameState.status}:${gameState.readingOrder.join('|')}`;
    if (signature === lastReadSignature) {
      return;
    }

    setSpeechMessage('Reading the list aloud…');
    const started = speakWords(gameState.readingOrder, setSpeechMessage, speakSettings);
    if (started) {
      setLastReadSignature(signature);
    }
  }, [gameState, lastReadSignature, speakSettings]);

  useEffect(() => {
    if (!gameState || gameState.status !== 'lobby') {
      return;
    }

    const myWord = gameState.words.find((word) => word.isYourWord && typeof word.text === 'string');
    if (!myWord) {
      return;
    }

    setWordInput((current) => (current.trim().length === 0 ? myWord.text ?? '' : current));
  }, [gameState]);

  const handleCreateGame = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const code = await api.createKingdomGame();
      setPendingCode(code);
      setStatusMessage(`Share this code with your group: ${code}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create a new game.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleJoinGame = useCallback(async () => {
    const code = pendingCode.trim();
    const name = nameInput.trim();
    if (code.length === 0 || name.length === 0) {
      setError('Enter both a game code and your name to join.');
      return;
    }

    setLoading(true);
    setError(null);
    setStatusMessage(null);

    try {
      const response: KingdomJoinResponse = await api.joinKingdomGame(code, name);
      const newSession: StoredSession = {
        code: response.code,
        playerId: response.playerId,
        playerToken: response.playerToken
      };
      setSession(newSession);
      setNameInput('');
      setStatusMessage(`Joined game ${response.code}. Waiting for everyone to add their word.`);
      await refreshState(newSession);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to join the game.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [nameInput, pendingCode, refreshState]);

  const handleSubmitWord = useCallback(async () => {
    if (!session) {
      setError('Join a game before submitting a word.');
      return;
    }

    const word = wordInput.trim();
    if (word.length === 0) {
      setError('Please enter a word before submitting.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const state = await api.submitKingdomWord(session.code, session.playerToken, word);
      setGameState(state);
      setStatusMessage('Your word has been saved.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit your word.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [session, wordInput]);

  const handleStartGame = useCallback(async () => {
    if (!session) {
      setError('Join a game before starting.');
      return;
    }

    setLoading(true);
    setError(null);
    setStatusMessage(null);

    try {
      const state = await api.startKingdomGame(session.code, session.playerToken);
      setGameState(state);
      setStatusMessage('The game has started!');
      setSpeechMessage('Reading the list aloud…');
      const started = speakWords(state.readingOrder, setSpeechMessage, speakSettings);
      if (started) {
        setLastReadSignature(`${state.status}:${state.readingOrder.join('|')}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to start the game.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [session, speakSettings]);

  const handleGuess = useCallback(async () => {
    if (!session) {
      setError('Join a game before guessing.');
      return;
    }

    if (!guessWordId || !guessPlayerId) {
      setError('Select a word and the player you want to guess.');
      return;
    }

    setLoading(true);
    setError(null);
    setGuessFeedback(null);

    try {
      const response: KingdomGuessResponse = await api.guessKingdomWord(
        session.code,
        session.playerToken,
        guessWordId,
        guessPlayerId
      );
      setGameState(response.state);

      const guessedPlayer = response.state.players.find((player) => player.playerId === guessPlayerId);
      const guessedWord = response.state.words.find((word) => word.wordId === guessWordId);

      if (response.correct && guessedPlayer && guessedWord) {
        setGuessFeedback(`✅ Correct! ${guessedPlayer.name} wrote "${guessedWord.text ?? 'their word'}".`);
        setGuessWordId('');
        setGuessPlayerId('');
      } else if (guessedPlayer) {
        setGuessFeedback(`❌ Not quite. ${guessedPlayer.name} did not write that word.`);
      } else {
        setGuessFeedback(response.correct ? '✅ Correct guess!' : '❌ Not quite right.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Guess could not be recorded.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [guessPlayerId, guessWordId, session]);

  const handleReset = useCallback(() => {
    setSession(null);
    setGameState(null);
    setPendingCode('');
    setWordInput('');
    setGuessPlayerId('');
    setGuessWordId('');
    setGuessFeedback(null);
    setStatusMessage(null);
    setSpeechMessage('');
    setError(null);
    setWordsHidden(false);
  }, []);

  const playersWaiting = useMemo(() => {
    if (!gameState) {
      return [] as string[];
    }

    return gameState.players.filter((player) => !player.wordSubmitted).map((player) => player.name);
  }, [gameState]);

  const availableWords = useMemo(() => {
    if (!gameState) {
      return [];
    }

    return gameState.words.filter((word) => !word.guessed);
  }, [gameState]);

  const guessablePlayers = useMemo(() => {
    if (!gameState) {
      return [];
    }

    return gameState.players.filter((player) => player.playerId !== gameState.yourPlayerId);
  }, [gameState]);

  return (
    <section className="page-section">
      <h2>Kingdom Word Game</h2>
      <p className="status-line">{formatStatusLabel(gameState)}</p>

      {statusMessage ? <p className="status-message">{statusMessage}</p> : null}
      {speechMessage ? <p className="status-message">{speechMessage}</p> : null}
      {error ? (
        <p role="alert" className="error-message">
          {error}
        </p>
      ) : null}

      {!session ? (
        <div className="kingdom-actions">
          <div className="kingdom-card">
            <h3>Create a New Game</h3>
            <button type="button" onClick={handleCreateGame} disabled={loading}>
              Generate Game Code
            </button>
          </div>

          <div className="kingdom-card">
            <h3>Join a Game</h3>
            <label htmlFor="kingdom-code-input">Game Code</label>
            <input
              id="kingdom-code-input"
              value={pendingCode}
              onChange={(event) => setPendingCode(event.target.value.toUpperCase())}
              placeholder="ABC123"
              autoComplete="off"
            />

            <label htmlFor="kingdom-name-input">Your Name</label>
            <input
              id="kingdom-name-input"
              value={nameInput}
              onChange={(event) => setNameInput(event.target.value)}
              placeholder="Player name"
              autoComplete="off"
            />

            <button type="button" onClick={handleJoinGame} disabled={loading}>
              Join Game
            </button>
          </div>
        </div>
      ) : (
        <div className="kingdom-session">
          <div className="kingdom-card">
            <h3>Game Details</h3>
            <p>
              <strong>Game Code:</strong> <span className="game-code">{session.code}</span>
            </p>
            <p>
              <strong>Your Player ID:</strong> {session.playerId}
            </p>
            <button type="button" onClick={handleReset}>
              Leave Game
            </button>
          </div>

          <div className="kingdom-card">
            <h3>Players</h3>
            {gameState ? (
              <ul>
                {gameState.players.map((player) => (
                  <li key={player.playerId}>
                    {player.name}{' '}
                    {player.playerId === gameState.yourPlayerId ? <em>(you)</em> : null}{' '}
                    {player.wordSubmitted ? '✅ word ready' : '⌛ waiting'}
                  </li>
                ))}
              </ul>
            ) : (
              <p>Waiting for player list…</p>
            )}

            {playersWaiting.length > 0 ? (
              <p>
                Still waiting on: {playersWaiting.join(', ')}
              </p>
            ) : null}
          </div>

          {gameState && gameState.status === 'lobby' ? (
            <div className="kingdom-card">
              <h3>Your Word</h3>
              <label htmlFor="kingdom-word-input">Enter the word you wrote down</label>
              <input
                id="kingdom-word-input"
                value={wordInput}
                onChange={(event) => setWordInput(event.target.value)}
                placeholder="Secret word"
                autoComplete="off"
                disabled={loading}
              />
              <button type="button" onClick={handleSubmitWord} disabled={loading}>
                Save Word
              </button>

              <button
                type="button"
                onClick={handleStartGame}
                disabled={loading || playersWaiting.length > 0 || !gameState}
              >
                Start Game &amp; Read Words
              </button>
            </div>
          ) : null}

          {gameState && gameState.status !== 'lobby' ? (
            <div className="kingdom-card">
              <div className="kingdom-card__header">
                <h3>Words in Play</h3>
                <button
                  type="button"
                  className="kingdom-word-toggle"
                  onClick={() => setWordsHidden((current) => !current)}
                  aria-pressed={wordsHidden}
                  disabled={gameState.words.length === 0}
                  title={wordsHidden ? 'Show the submitted words' : 'Hide the submitted words'}
                >
                  {wordsHidden ? 'Show Word List' : 'Hide Word List'}
                </button>
              </div>
              {gameState.words.length === 0 ? (
                <p>Words will appear here once everyone has submitted them.</p>
              ) : (
                <>
                  {wordsHidden ? (
                    <p className="kingdom-word-list__notice">
                      Word list hidden. Select &ldquo;Show Word List&rdquo; when you&rsquo;re ready to view it.
                    </p>
                  ) : null}
                  <ul
                    className={`kingdom-word-list${wordsHidden ? ' kingdom-word-list--hidden' : ''}`}
                    aria-hidden={wordsHidden}
                  >
                    {gameState.words.map((word) => {
                      const ownerName = word.ownerPlayerId
                        ? gameState.players.find((player) => player.playerId === word.ownerPlayerId)?.name ?? 'Unknown'
                        : null;
                      const dictionaryStatus = word.dictionaryVerified;

                      return (
                        <li key={word.wordId} className="kingdom-word-item">
                          <div className="kingdom-word-item__header">
                            <strong>{word.text ?? 'Hidden until game starts'}</strong>
                            {word.isYourWord ? <span className="kingdom-word-item__tag">(yours)</span> : null}
                            {dictionaryStatus === true ? (
                              <span
                                className="kingdom-word-item__status kingdom-word-item__status--verified"
                                aria-label="Word verified in dictionary"
                                title="Word verified in dictionary"
                              >
                                ✓
                              </span>
                            ) : null}
                            {dictionaryStatus === false ? (
                              <span
                                className="kingdom-word-item__status kingdom-word-item__status--unverified"
                                aria-label="Word not found in dictionary"
                                title="Word not found in dictionary"
                              >
                                ✗
                              </span>
                            ) : null}
                          </div>

                          {dictionaryStatus === true && word.dictionaryDefinition ? (
                            <p className="kingdom-word-item__definition">
                              {word.dictionaryDefinition}{' '}
                              {word.dictionaryUrl ? (
                                <a
                                  href={word.dictionaryUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View dictionary entry
                                </a>
                              ) : null}
                            </p>
                          ) : null}

                          {dictionaryStatus === false ? (
                            <p className="kingdom-word-item__definition kingdom-word-item__definition--unverified">
                              We couldn't find this word in the dictionary.
                            </p>
                          ) : null}

                          {word.guessed && ownerName ? (
                            <p className="kingdom-word-item__result">
                              Guessed correctly! ({ownerName})
                            </p>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              <div className="kingdom-audio-settings">
                <div className="kingdom-audio-settings__group">
                  <label htmlFor="kingdom-repeat-count-input">Repeat Count</label>
                  <input
                    id="kingdom-repeat-count-input"
                    type="number"
                    min={1}
                    max={10}
                    step={1}
                    value={repeatCount}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      if (Number.isNaN(next)) {
                        setRepeatCount(1);
                        return;
                      }
                      setRepeatCount(Math.min(10, Math.max(1, Math.round(next))));
                    }}
                  />
                </div>

                <div className="kingdom-audio-settings__group">
                  <label htmlFor="kingdom-speed-input">
                    Speech Speed{' '}
                    <span className="kingdom-audio-settings__value">{speechRate.toFixed(1)}×</span>
                  </label>
                  <input
                    id="kingdom-speed-input"
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={speechRate}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      setSpeechRate(Number.isNaN(next) ? 1 : Math.min(2, Math.max(0.5, next)));
                    }}
                  />
                </div>

                <div className="kingdom-audio-settings__group">
                  <label htmlFor="kingdom-pause-input">
                    Pause Between Words{' '}
                    <span className="kingdom-audio-settings__value">{(pauseDurationMs / 1000).toFixed(2)}s</span>
                  </label>
                  <input
                    id="kingdom-pause-input"
                    type="range"
                    min="0"
                    max="3000"
                    step="100"
                    value={pauseDurationMs}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      setPauseDurationMs(Number.isNaN(next) ? 0 : Math.min(3000, Math.max(0, Math.round(next))));
                    }}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setSpeechMessage('Reading the list aloud…');
                  speakWords(gameState.readingOrder, setSpeechMessage, speakSettings);
                }}
                disabled={gameState.readingOrder.length === 0}
              >
                Replay Word List
              </button>
            </div>
          ) : null}

          {gameState && gameState.status === 'started' ? (
            <div className="kingdom-card">
              <h3>Make a Guess</h3>
              <label htmlFor="guess-word-select">Which word?</label>
              <select
                id="guess-word-select"
                value={guessWordId}
                onChange={(event) => setGuessWordId(event.target.value)}
              >
                <option value="">Select a word</option>
                {availableWords.map((word) => (
                  <option key={word.wordId} value={word.wordId}>
                    {word.text ?? 'Hidden'}
                  </option>
                ))}
              </select>

              <label htmlFor="guess-player-select">Who wrote it?</label>
              <select
                id="guess-player-select"
                value={guessPlayerId}
                onChange={(event) => setGuessPlayerId(event.target.value)}
              >
                <option value="">Select a player</option>
                {guessablePlayers.map((player) => (
                  <option key={player.playerId} value={player.playerId}>
                    {player.name}
                  </option>
                ))}
              </select>

              <button type="button" onClick={handleGuess} disabled={loading || availableWords.length === 0}>
                Submit Guess
              </button>

              {guessFeedback ? <p className="status-message">{guessFeedback}</p> : null}
            </div>
          ) : null}

          {gameState && gameState.status === 'completed' ? (
            <div className="kingdom-card">
              <h3>All Words Guessed!</h3>
              <p>Great job! Everyone&apos;s words have been matched.</p>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

